import React, { useEffect, useRef, useState } from "react";
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import {useTranslate} from "@tolgee/react";
import {UPGRADE_SUBSCRIPTION_URL} from "../../../../config/constants";

interface UpgradeRequiredTooltipProps {
    show: boolean;
    /**
     * How the hint is raised. 'hover' is the default and keeps the original
     * behaviour; 'click' only reveals it once the locked control is actually
     * clicked, so it cannot fire from the pointer crossing a row.
     */
    trigger?: 'hover' | 'click';
    children: React.ReactElement;
}

const UpgradeRequiredTooltipWrapper: React.FC<UpgradeRequiredTooltipProps> = ({ show, trigger = 'hover', children }) => {
    const { t } = useTranslate();
    const [visible, setVisible] = useState(false);
    const wrapperRef = useRef<HTMLSpanElement>(null);

    // Click mode keeps its own visibility instead of letting the overlay close
    // itself: react-bootstrap's rootClose would be shut again by the very click
    // that opened it whenever this sits inside a modal (portal) — the same trap
    // documented on the default settings dropdown. Listening on mousedown only
    // while visible cannot self-close.
    useEffect(() => {
        if (trigger !== 'click' || !visible) return undefined;

        const onDocumentMouseDown = (event: MouseEvent) => {
            if (!wrapperRef.current?.contains(event.target as Node)) setVisible(false);
        };
        document.addEventListener('mousedown', onDocumentMouseDown);
        return () => { document.removeEventListener('mousedown', onDocumentMouseDown); };
    }, [trigger, visible]);

    useEffect(() => {
        if (!show) setVisible(false);
    }, [show]);

    const overlay = show ? ( // Show tooltip only if disabled
        <Tooltip id={`tooltip-`}>
            <div>
                {t(
                    "lomavisCreator.multipostSettings.featureNotAvailable"
                )}
            </div>

            <div className="w-100 mt-2 text-end">
                <a
                    href={UPGRADE_SUBSCRIPTION_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="text-nowrap"
                >
                    {t("lomavisCreator.multipostSettings.upgradeNow")}
                </a>
            </div>
        </Tooltip>
    ) : (
        <></>
    );

    if (trigger === 'click') {
        return (
            <span
                ref={wrapperRef}
                className="d-inline-block w-100"
                onClick={() => { if (show) setVisible((wasVisible) => !wasVisible); }}
            >
                <OverlayTrigger
                    placement="auto"
                    trigger={[]}
                    show={visible && show}
                    overlay={overlay}
                >
                    {children}
                </OverlayTrigger>
            </span>
        );
    }

    return (
        <OverlayTrigger
            delay={{ show: 10, hide: 1000 }}
            placement="auto"
            overlay={overlay}
        >
            {children}

        </OverlayTrigger>
    );
};

export default UpgradeRequiredTooltipWrapper;
